import React, { useEffect, useState } from "react";
import { Plus, Mail, Phone, Building2, Briefcase, Trash2, CalendarPlus, X } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const emptyCompany = { company_name: "", contact_name: "", phone: "", email: "", notes: "" };
const emptyBooking = { property_name: "", check_in: "", check_out: "", value: "", notes: "" };

function CompanyDialog({ trigger, initial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCompany);
  useEffect(() => { if (open) setForm(initial ? { ...emptyCompany, ...initial } : emptyCompany); }, [open]);
  const save = async () => {
    try {
      if (initial?.id) await api.put(`/corporate/${initial.id}`, form);
      else await api.post("/corporate", form);
      toast.success("Saved"); setOpen(false); onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-head">{initial?.id ? "Edit Company" : "New Company"}</DialogTitle>
          <DialogDescription className="sr-only">Corporate client company details.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="overline">Company name</Label><Input data-testid="corp-company-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1.5" /></div>
          <div><Label className="overline">Contact name</Label><Input data-testid="corp-contact-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Phone</Label><Input data-testid="corp-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Email</Label><Input data-testid="corp-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div><Label className="overline">Notes</Label><Textarea data-testid="corp-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="Marketing notes, preferences, key account info…" /></div>
        </div>
        <DialogFooter><Button data-testid="save-company-button" onClick={save} disabled={!form.company_name}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyDetail({ company, onChanged, canDelete }) {
  const [open, setOpen] = useState(false);
  const [c, setC] = useState(company);
  const [bk, setBk] = useState(emptyBooking);
  useEffect(() => { if (open) setC(company); }, [open, company]);
  const refresh = (data) => { setC(data); onChanged(); };
  const addBooking = async () => {
    if (!bk.property_name.trim()) return;
    try {
      const { data } = await api.post(`/corporate/${c.id}/bookings`, { ...bk, value: bk.value === "" ? 0 : Number(bk.value) });
      setBk(emptyBooking); refresh(data); toast.success("Booking added");
    } catch (e) { toast.error(apiError(e)); }
  };
  const delBooking = async (bid) => {
    try { const { data } = await api.delete(`/corporate/${c.id}/bookings/${bid}`); refresh(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const bookings = c.bookings || [];
  const total = bookings.reduce((s, b) => s + (Number(b.value) || 0), 0);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 text-xs" data-testid={`corp-manage-${company.id}`}><CalendarPlus className="h-3.5 w-3.5 mr-1" />Bookings</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-head">{c.company_name}</DialogTitle>
          <DialogDescription className="sr-only">Manage manual bookings for this corporate client.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[68vh] overflow-y-auto thin-scroll pr-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{bookings.length} booking(s)</span>
            <span>Total value: <strong className="text-foreground">£{total.toLocaleString()}</strong></span>
          </div>
          <div className="space-y-2">
            {bookings.map((b) => (
              <div key={b.id} data-testid={`corp-booking-${b.id}`} className="border border-border rounded-md p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{b.property_name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{b.check_in ? fmtDate(b.check_in) : "—"} → {b.check_out ? fmtDate(b.check_out) : "—"}{b.value ? ` · £${Number(b.value).toLocaleString()}` : ""}</p>
                    {b.notes && <p className="text-xs mt-1 whitespace-pre-wrap">{b.notes}</p>}
                  </div>
                  <button data-testid={`corp-booking-del-${b.id}`} onClick={() => delBooking(b.id)}><Trash2 className="h-3.5 w-3.5 text-[#9E382A]" /></button>
                </div>
              </div>
            ))}
            {bookings.length === 0 && <p className="text-xs text-muted-foreground text-center py-3">No bookings logged yet.</p>}
          </div>
          <div className="border-t border-border pt-3 space-y-2">
            <p className="overline">Add booking</p>
            <Input data-testid="corp-bk-property" value={bk.property_name} onChange={(e) => setBk({ ...bk, property_name: e.target.value })} placeholder="Property / address" />
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="overline text-[0.6rem]">Check-in</Label><Input data-testid="corp-bk-checkin" type="date" value={bk.check_in} onChange={(e) => setBk({ ...bk, check_in: e.target.value })} className="mt-1" /></div>
              <div><Label className="overline text-[0.6rem]">Check-out</Label><Input data-testid="corp-bk-checkout" type="date" value={bk.check_out} onChange={(e) => setBk({ ...bk, check_out: e.target.value })} className="mt-1" /></div>
            </div>
            <Input data-testid="corp-bk-value" type="number" value={bk.value} onChange={(e) => setBk({ ...bk, value: e.target.value })} placeholder="Value £ (optional)" />
            <Textarea data-testid="corp-bk-notes" rows={2} value={bk.notes} onChange={(e) => setBk({ ...bk, notes: e.target.value })} placeholder="Notes (optional)" />
            <Button className="w-full" data-testid="add-corp-booking" onClick={addBooking} disabled={!bk.property_name.trim()}><Plus className="h-4 w-4 mr-1" />Add booking</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Corporate() {
  const { user } = useAuth();
  const canManage = ["admin", "operations"].includes(user.role);
  const canDelete = user.role === "admin";
  const [companies, setCompanies] = useState([]);
  const load = () => api.get("/corporate").then((r) => setCompanies(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.company_name} and all its bookings?`)) return;
    try { await api.delete(`/corporate/${c.id}`); toast.success("Company deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="overline text-accent">Corporate clients</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Corporate</h1>
          <p className="text-sm text-muted-foreground mt-1">Companies that book long-term for contractors and staff — track key accounts and their bookings.</p>
        </div>
        {canManage && <CompanyDialog onSaved={load} trigger={<Button data-testid="new-company-button"><Plus className="h-4 w-4 mr-1" />New Company</Button>} />}
      </div>

      {(() => {
        const totalValue = companies.reduce((s, c) => s + (c.bookings || []).reduce((x, b) => x + (Number(b.value) || 0), 0), 0);
        const totalBookings = companies.reduce((s, c) => s + (c.bookings || []).length, 0);
        return (
          <div className="grid grid-cols-3 gap-4" data-testid="corporate-summary">
            <div className="bg-white border border-border rounded-md p-4">
              <p className="overline">Companies</p>
              <p className="font-head text-2xl font-extrabold mt-1" data-testid="summary-companies">{companies.length}</p>
            </div>
            <div className="bg-white border border-border rounded-md p-4">
              <p className="overline">Bookings</p>
              <p className="font-head text-2xl font-extrabold mt-1" data-testid="summary-bookings">{totalBookings}</p>
            </div>
            <div className="bg-white border border-border rounded-md p-4 border-l-2 border-l-[#2B4C3F]">
              <p className="overline">Total booking value</p>
              <p className="font-head text-2xl font-extrabold mt-1 text-[#2B4C3F]" data-testid="summary-total-value">£{totalValue.toLocaleString()}</p>
            </div>
          </div>
        );
      })()}

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-left">
              <th className="px-4 py-3 overline font-semibold">Company</th>
              <th className="px-4 py-3 overline font-semibold">Contact</th>
              <th className="px-4 py-3 overline font-semibold">Bookings</th>
              <th className="px-4 py-3 overline font-semibold w-40"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {companies.map((c) => (
              <tr key={c.id} data-testid={`company-row-${c.id}`} className="hover:bg-secondary/40 transition-colors">
                <td className="px-4 py-3 font-medium"><span className="flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-muted-foreground" />{c.company_name}</span></td>
                <td className="px-4 py-3 text-muted-foreground">
                  <div className="flex flex-col gap-0.5">
                    {c.contact_name && <span className="text-xs">{c.contact_name}</span>}
                    {c.email && <span className="flex items-center gap-1 text-xs"><Mail className="h-3 w-3" />{c.email}</span>}
                    {c.phone && <span className="flex items-center gap-1 text-xs"><Phone className="h-3 w-3" />{c.phone}</span>}
                  </div>
                </td>
                <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary">{(c.bookings || []).length} booking(s)</span>{(c.bookings || []).length > 0 && <span className="ml-2 text-xs text-muted-foreground">£{(c.bookings || []).reduce((s, b) => s + (Number(b.value) || 0), 0).toLocaleString()}</span>}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <CompanyDetail company={c} onChanged={load} canDelete={canDelete} />
                    {canManage && <CompanyDialog initial={c} onSaved={load} trigger={<Button size="sm" variant="ghost" className="h-8 text-xs" data-testid={`edit-company-${c.id}`}>Edit</Button>} />}
                    {canDelete && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`del-company-${c.id}`} onClick={() => remove(c)}><Trash2 className="h-3.5 w-3.5 text-[#9E382A]" /></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {companies.length === 0 && <p className="p-10 text-center text-sm text-muted-foreground">No corporate clients yet. Add your first company.</p>}
      </div>
    </div>
  );
}
